<template>
  <v-card>
    <toolbar-dialog title="user.changeYourPassword" @close="cancel" />
    <v-card-text>
      <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
    </v-card-text>
    <v-card-text>
      <v-form ref="form" autocomplete="off" v-model="valid" @submit.prevent="submit">
        <v-col cols="12">
          <v-text-field
            id="profile-current-password"
            prepend-icon="lock"
            :append-icon="showCurrentPassword ? 'visibility' : 'visibility_off'"
            :type="showCurrentPassword ? 'text' : 'password'"
            @click:append="showCurrentPassword = !showCurrentPassword"
            v-model="form.currentPassword"
            :rules="requiredRule"
            :label="$t('user.label.currentPassword')"
            :placeholder="$t('user.label.currentPassword')"
            autocomplete="new-password"
            color="secondary"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            id="profile-new-password"
            prepend-icon="lock"
            :append-icon="showNewPassword ? 'visibility' : 'visibility_off'"
            :type="showNewPassword ? 'text' : 'password'"
            @click:append="showNewPassword = !showNewPassword"
            v-model="form.newPassword"
            :rules="newPasswordRules"
            :label="$t('user.label.newPassword')"
            :placeholder="$t('user.label.newPassword')"
            autocomplete="new-password"
            color="secondary"
          />
          <div
            class="password-requirements mt-2"
            v-if="form.newPassword != null && String(form.newPassword).trim() !== ''"
          >
            <div class="caption mb-1">{{ $t('auth.passwordRequirements') }}:</div>
            <div class="requirement-line" :class="{ 'requirement-ok': passwordChecks.minLength }">
              <v-icon x-small :color="passwordChecks.minLength ? 'success' : 'grey'">
                {{ passwordChecks.minLength ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>{{ $t('auth.passwordMinLength') }}</span>
            </div>
            <div class="requirement-line" :class="{ 'requirement-ok': passwordChecks.hasNumber }">
              <v-icon x-small :color="passwordChecks.hasNumber ? 'success' : 'grey'">
                {{ passwordChecks.hasNumber ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>{{ $t('auth.passwordMustIncludeNumber') }}</span>
            </div>
            <div class="requirement-line" :class="{ 'requirement-ok': passwordChecks.hasSpecial }">
              <v-icon x-small :color="passwordChecks.hasSpecial ? 'success' : 'grey'">
                {{ passwordChecks.hasSpecial ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
              <span>{{ $t('auth.passwordMustIncludeSpecial') }}</span>
            </div>
          </div>
        </v-col>
        <v-col cols="12">
          <v-text-field
            id="profile-password_verify"
            prepend-icon="lock"
            :append-icon="showRepeatPassword ? 'visibility' : 'visibility_off'"
            :type="showRepeatPassword ? 'text' : 'password'"
            @click:append="showRepeatPassword = !showRepeatPassword"
            v-model="form.passwordVerify"
            :rules="passwordMatchRules"
            :label="$t('user.label.repeatPassword')"
            :placeholder="$t('user.label.repeatPassword')"
            autocomplete="new-password"
          />
        </v-col>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <close-button text="common.cancel" @click="cancel"></close-button>
      <submit-button @click="submit" :loading="loading" :disabled="!valid"></submit-button>
    </v-card-actions>
  </v-card>
</template>

<script>
import ProfileProvider from '@dracul/user-frontend/src/providers/ProfileProvider';
import { SubmitButton, CloseButton, ToolbarDialog } from '@dracul/common-frontend';
import { passwordStrengthRule, getPasswordRequirementChecks } from '@/utils/passwordValidation';

export default {
  name: 'ProfilePasswordFormInvira',
  components: { ToolbarDialog, SubmitButton, CloseButton },
  data() {
    return {
      loading: false,
      valid: true,
      errorMessage: null,
      showCurrentPassword: false,
      showNewPassword: false,
      showRepeatPassword: false,
      form: {
        currentPassword: null,
        newPassword: null,
        passwordVerify: null,
      },
      requiredRule: [v => !!v || this.$t('user.validation.required')],
    };
  },
  computed: {
    newPasswordRules() {
      return [
        v => (v != null && String(v).trim() !== '') || this.$t('user.validation.required'),
        passwordStrengthRule(this),
      ];
    },
    passwordChecks() {
      return getPasswordRequirementChecks(this.form.newPassword);
    },
    passwordMatchRules() {
      return [
        v => !!v || this.$t('user.validation.required'),
        () =>
          (this.form.newPassword === this.form.passwordVerify) ||
          this.$t('user.validation.passwordVerify'),
      ];
    },
  },
  methods: {
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.errorMessage = null;
      this.loading = true;
      try {
        const response = await ProfileProvider.changePassword(
          this.form.currentPassword,
          this.form.newPassword
        );
        const status =
          response.data && response.data.changePassword && response.data.changePassword.status;
        if (status) {
          await this.$store.dispatch('fetchMe');
          this.$emit('success');
        } else {
          this.errorMessage =
            this.$t('auth.badCredentials') || 'Error al cambiar la contraseña';
        }
      } catch (err) {
        const msg =
          err.graphQLErrors && err.graphQLErrors[0] && err.graphQLErrors[0].message;
        this.errorMessage = msg || err.message || this.$t('auth.badCredentials') || 'Error';
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.$emit('close');
      if (this.$refs.form) this.$refs.form.reset();
      this.form.currentPassword = null;
      this.form.newPassword = null;
      this.form.passwordVerify = null;
    },
  },
};
</script>

<style scoped>
.password-requirements {
  padding-left: 4px;
}
.requirement-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}
.requirement-line .v-icon {
  flex-shrink: 0;
}
.requirement-line span {
  font-size: 0.75rem;
}
.requirement-ok span {
  color: var(--v-success-base, #4caf50);
}
</style>
